import { endpoints, spainAuthenticationEndpoints } from '@/core/endpoints';
import { CountryCodes } from '@/models/countries';
import { StorageKeys } from '@/models/storage-keys';
import { UserProducts } from '@/models/user-settings';
import { StorageUtils } from '@/utils/storage.utils';
import { AxiosError } from 'axios';
import httpService from './HttpService';

interface LoginResponse {
  token: string;
  expiration: string;
}

/**
 * AuthService class provides methods to handle authentication.
 * It supports login, logout, and token management.
 *
 * @class AuthService
 */
class AuthService {
  /**
   * Logs in the user with the provided username and password.
   * If successful, stores the token and sets the Authorization header.
   * @param {string} username - The username of the user.
   * @param {string} password - The password of the user.
   * @returns {Promise<LoginResponse>} - A promise that resolves to the login response.
   * @throws {string | Error} - Throws an error if the login fails.
   */
  public async login(
    username: string,
    password: string
  ): Promise<LoginResponse> {
    try {
      const response = await httpService.post<LoginResponse>(
        endpoints.AUTHTENTICATION,
        {
          username,
          password,
          country: CountryCodes.FRANCE
        }
      );
      if (response.token) {
        httpService.setAuthorizationHeader(response.token);
        StorageUtils.setStorageItem(StorageKeys.USER_TOKEN, response.token);
      }
      return response;
    } catch (error) {
      if (
        ((error as AxiosError).status === 400 ||
          (error as AxiosError).status === 401) &&
        (error as AxiosError).response?.data
      ) {
        throw ((error as AxiosError).response!.data as Array<string>)[0];
      }
      throw error;
    }
  }

  /**
   * Checks if the user is authenticated in Spain.
   *
   * This function sends a GET request to the endpoint defined in
   * `spainAuthenticationEndpoints.IS_AUTHENTICATED` to verify
   * if the user is authenticated in the Spanish system.
   *
   * @returns A promise that resolves to `true` if the user is authenticated,
   *          or `false` if the user is not authenticated or an error occurs.
   */
  public async isAuthenticatedInSpain(): Promise<boolean> {
    try {
      await httpService.get<boolean>(
        spainAuthenticationEndpoints.IS_AUTHENTICATED
      );
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Retrieves the products associated with a Spanish user.
   *
   * This method sends a GET request to the specified endpoint to fetch
   * the user's products. The response is expected to conform to the
   * `UserProducts` type.
   *
   * @returns {Promise<UserProducts>} A promise that resolves to the user's products.
   * @throws {Error} If the HTTP request fails or the response is invalid.
   */
  public async getSpainUserProducts(): Promise<UserProducts> {
    const response = await httpService.get<UserProducts>(
      spainAuthenticationEndpoints.GET_PRODUCTS
    );

    return response;
  }

  /**
   * Logs the user out of the France authentication system by removing the token
   * from storage and clearing the Authorization header in the HttpService.
   * This method does not send a request to the server, as it is assumed that
   * the session is managed client-side.
   */
  public franceLogout(): void {
    httpService.setAuthorizationHeader('');
  }
}

const authService = new AuthService();
export default authService;
